import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Image } from './Image'
import { FKType } from './SQLBuilder'
import { User } from './User'

type PostConstructorArgs = ConstructorArgs<() => Post>

export enum POST_FIELD {
    POST_FIELD_IMAGE,
    POST_FIELD_TITLE,
    POST_FIELD_MEMO,
    POST_FIELD_BUTTON,
    POST_FIELD_SCHEDULE,
    POST_FIELD_AUDIENCE,
    POST_FIELD_STATUS_BAR,
    POST_FIELD_DELETE,
    POST_FIELD_SPACER,
}

export class Post extends BaseRecord {
    readonly recordType = 'post'

    date?: moment.Moment
    image?: Image
    memo?: string
    title?: string
    type?: string
    link?: string
    income?: string
    button?: string
    drip?: number // Integer
    schedule?: string
    archivedDate?: moment.Moment
    cancelledDate?: moment.Moment
    completedDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    readonly reach: number = 0
    readonly views: number = 0
    readonly opens: number = 0
    readonly clicks: number = 0
    readonly shares: number = 0

    constructor(data?: Partial<PostConstructorArgs>) {
        super(data)
        if (!data) {
            this.type = 'link'
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.date = this.propertyFromTimestampArg(data.date)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.memo = data.memo ?? undefined
        this.title = data.title ?? undefined
        this.type = data.type ?? undefined
        this.link = data.link ?? undefined
        this.income = data.income ?? undefined
        this.button = data.button ?? undefined
        this.drip = this.propertyFromIntArg(data.drip)
        this.schedule = data.schedule ?? undefined
        this.archivedDate = this.propertyFromTimestampArg(data.archived_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)

        this.reach = this.propertyFromIntArg(data.reach) ?? 0
        this.views = this.propertyFromIntArg(data.views) ?? 0
        this.opens = this.propertyFromIntArg(data.opens) ?? 0
        this.clicks = this.propertyFromIntArg(data.clicks) ?? 0
        this.shares = this.propertyFromIntArg(data.shares) ?? 0
    }

    clone(overrides?: Partial<PostConstructorArgs>): Post {
        return new Post({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Post {
        return CacheFactory.getInstance().getRecord(Post, id) ?? new Post({ id })
    }

    get subtitle(): string {
        if (this.clicks > 10) return `Engaged by ${this.clicks} members`
        if (this.opens > 10) return `Opened by ${this.opens} members`
        if (this.views > 10) return `Viewed by ${this.views} members`
        if (this.reach > 10) return `Reached ${this.reach} members`
        if (this.completedDate) return `Completed on ${this.completedDate.format('MMM D, YYYY')}`
        return `Created on ${this.createdDate!.format('MMM D, YYYY')}`
    }

    get scheduleString(): string {
        if (this.schedule === 'drip') return `Drip campaign post (D${this.drip ?? 0})`
        if (this.schedule === 'date') return `Schedule on ${this.date?.format('MMM D, YYYY')}`
        return this.date ? 'Post now' : ''
    }

    get audienceString(): string {
        if (this.income === 'new') return 'Under $2k monthly income'
        if (this.income === 'vip') return 'Above $2k monthly income'
        return ''
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.date) this.date = moment(data.date)
        if (data.archivedDate) this.archivedDate = moment(data.archivedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        images: ['image'],
    }

    static dataType(): DataType {
        return 'posts' as DataType
    }

    jsonObject(): any {
        const jsonObject = super.jsonObject()
        jsonObject['owner'] = this.owner?.id
        jsonObject['date'] = this.date?.format()
        jsonObject['memo'] = this.memo ?? ''
        jsonObject['type'] = this.type ?? ''
        jsonObject['link'] = this.link ?? ''
        jsonObject['title'] = this.title ?? ''
        jsonObject['income'] = this.income ?? ''
        jsonObject['button'] = this.button ?? ''
        jsonObject['schedule'] = this.schedule ?? ''
        jsonObject['drip'] = this.drip ?? ''
        if (!this.id && this.image && this.image.id) jsonObject['image'] = this.image.id
        return jsonObject
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
